import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context.js';
import { ApiError } from '../lib/api.js';

const MESSAGES: Record<string, string> = {
  sso_disabled: 'O acesso pelo NxNet não está habilitado neste ambiente.',
  invalid_token: 'O acesso expirou ou é inválido. Volte ao NxNet e clique em NxVolt novamente.',
  inactive_user: 'Seu usuário está inativo no NxVolt. Fale com o administrador.',
  sso_not_allowed: 'O acesso ao NxVolt ainda não está liberado para o seu usuário.',
  sso_upstream: 'Não foi possível validar seu acesso com o NxNet. Tente novamente em instantes.',
  'Too Many Requests': 'Muitas tentativas de acesso. Aguarde alguns minutos e tente de novo.',
};

const STEPS = ['Validando seu acesso', 'Carregando sua operadora', 'Abrindo o painel'];

export function SsoPage() {
  const [params] = useSearchParams();
  const { loginWithSso } = useAuth();
  const token = params.get('token');
  const [state, setState] = useState<{ kind: 'pending' } | { kind: 'done' } | { kind: 'error'; message: string }>(
    token ? { kind: 'pending' } : { kind: 'error', message: 'Link de acesso incompleto.' },
  );
  const [step, setStep] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (state.kind !== 'pending') return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 700);
    return () => clearInterval(t);
  }, [state.kind]);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    const startedAt = Date.now();
    loginWithSso(token)
      .then(() => {
        const wait = Math.max(0, 1400 - (Date.now() - startedAt));
        setTimeout(() => setState({ kind: 'done' }), wait);
      })
      .catch((e: unknown) => {
        const code = e instanceof ApiError ? e.code : 'network';
        setState({ kind: 'error', message: MESSAGES[code] ?? 'Não foi possível entrar. Tente novamente.' });
      });
  }, [token, loginWithSso]);

  if (state.kind === 'done') return <Navigate to="/app" replace />;

  return (
    <div className="nx-sso">
      <div className="nx-sso__panel" role="status" aria-live="polite">
        <img src="/logo-noxxon-dark.png" alt="Noxxon Sat" className="nx-sso__logo" />
        {state.kind === 'pending' ? (
          <>
            <div className="nx-sso__ring" aria-hidden="true">
              <span />
            </div>
            <h1 className="nx-sso__title">Entrando no NxVolt</h1>
            <p className="nx-sso__step">{STEPS[step]}…</p>
            <ol className="nx-sso__steps" aria-hidden="true">
              {STEPS.map((label, i) => (
                <li key={label} className={i < step ? 'is-done' : i === step ? 'is-active' : ''}>
                  {label}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <>
            <h1 className="nx-sso__title">Não foi possível entrar</h1>
            <p className="nx-sso__error">{state.message}</p>
            <Link to="/login" className="nx-sso__link">
              Entrar com e-mail e senha
            </Link>
          </>
        )}
      </div>
      <div className="nx-sso__foot">NxVolt · uma solução Noxxon Sat</div>
    </div>
  );
}
